import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import type { ApiMutationData, BaseResponse } from './api.types';

export type ApiServiceKey = 'api' | 'restaurant' | 'fuel' | 'schedule';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  private baseUrlFor(service: ApiServiceKey = 'api'): string {
    const url =
      service === 'restaurant'
        ? environment.restaurantUrl
        : service === 'fuel'
          ? environment.fuelUrl
          : service === 'schedule'
            ? environment.scheduleUrl
            : environment.apiUrl;
    return url?.endsWith('/') ? url : url + '/';
  }

  /**
   * POST endpoint/filter with filter body; returns full response (including query/paging).
   * When `service` is `fuel`, the POST body matches legacy: inner `filter` object (not the wrapper).
   */
  getFilteredData<T = object>(
    endpoint: string,
    filter: object,
    options: { hasFilter?: boolean; filterChild?: string; service?: ApiServiceKey } = {}
  ): Observable<BaseResponse<T>> {
    const { hasFilter = true, filterChild, service = 'api' } = options;
    let body: object = filter;

    if (service === 'fuel') {
      const wrapped = filter as {
        filter?: Record<string, object | string | number | boolean | null | undefined>;
        query?: { paging?: { offset?: number; limit?: number } };
        parameters?: object[];
      };
      if (wrapped?.filter) {
        const paging = wrapped.query?.paging;
        if (paging?.offset !== undefined) {
          wrapped.filter['_offset'] = paging.offset;
        }
        if (paging?.limit !== undefined) {
          wrapped.filter['_page_size'] = paging.limit;
        }
        if (wrapped.parameters?.length) {
          wrapped.filter['_parameters'] = wrapped.parameters;
        }
        body = wrapped.filter;
      }
    }

    const childSuffix = filterChild && typeof filterChild === 'string' ? '/' + filterChild : '';
    const path = endpoint + (hasFilter ? '/filter' : '') + childSuffix;
    return this.http
      .post<BaseResponse<T>>(this.baseUrlFor(service) + path, body)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * POST endpoint/date with empty body; returns response.data (e.g. currency rate by date).
   */
  getFilteredDataByDate<T = object>(endpoint: string, date: string): Observable<T> {
    const path = `${endpoint}/${date}`;
    return this.http.post<BaseResponse<T>>(this.baseUrlFor('api') + path, {}).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * GET endpoint/id or endpoint/id/childEndpoint; returns response.data.
   */
  getDataById<T = object>(
    endpoint: string,
    id: string | number,
    childEndpoint?: string,
    service: ApiServiceKey = 'api'
  ): Observable<T> {
    let path = `${endpoint}/${id}`;
    if (childEndpoint) path += `/${childEndpoint}`;
    return this.http.get<BaseResponse<T>>(this.baseUrlFor(service) + path).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * GET path; returns response body as-is (for endpoints that don't use BaseResponse).
   */
  get<T = object>(path: string, service: ApiServiceKey = 'api'): Observable<T> {
    return this.http.get<T>(this.baseUrlFor(service) + path).pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * GET endpoint; returns response.data.
   */
  getAllData<T = object>(endpoint: string, service: ApiServiceKey = 'api'): Observable<T> {
    return this.http.get<BaseResponse<T>>(this.baseUrlFor(service) + endpoint).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * GET {endpoint}/{id}/persist — created/modified user and timestamps (Kayıt Bilgisi).
   */
  getChanges<T = object>(endpoint: string, id: string | number, service: ApiServiceKey = 'api'): Observable<T> {
    const path = `${endpoint}/${id}/${environment.changesSelector}`;
    return this.http.get<BaseResponse<T>>(this.baseUrlFor(service) + path).pipe(
      map((response) => response?.data as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * GET {endpoint}/generatecode — same path as legacy `ApiService.generateCode` (no hyphen).
   */
  generateCode<T = { value?: string } | string>(endpoint: string, service: ApiServiceKey = 'api'): Observable<T> {
    return this.getDataById<T>(endpoint, environment.generateCodeSelector, undefined, service);
  }

  /**
   * GET {endpoint}/{id}/lock — same as legacy `ApiService.lockData` (before opening edit).
   */
  lockData<T = object>(endpoint: string, id: string | number, service: ApiServiceKey = 'api'): Observable<T> {
    return this.getDataById<T>(endpoint, id, environment.entityLockSelector, service);
  }

  /**
   * GET {endpoint}/{id}/unlock — same as legacy `ApiService.unlockData` (not PUT …/unlock/{id}).
   */
  unlockData<T = object>(endpoint: string, id: string | number, service: ApiServiceKey = 'api'): Observable<T> {
    return this.getDataById<T>(endpoint, id, environment.entityUnlockSelector, service);
  }

  /**
   * POST endpoint with body; returns response.data.
   */
  addData<T = ApiMutationData>(endpoint: string, payload: object, service: ApiServiceKey = 'api'): Observable<T> {
    return this.http.post<BaseResponse<T>>(this.baseUrlFor(service) + endpoint, payload).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * POST `endpoint`/`id` with body; returns response.data.
   * Used for updates when the backend does not allow PUT on the resource (e.g. jretail `pos-credit-card/{id}`).
   */
  postDataById<T = ApiMutationData>(
    endpoint: string,
    id: string | number,
    payload: object,
    service: ApiServiceKey = 'api'
  ): Observable<T> {
    const path = `${endpoint}/${id}`;
    return this.http.post<BaseResponse<T>>(this.baseUrlFor(service) + path, payload).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * POST `path` with JSON body (legacy `getSpecialFilteredData` — no `/filter` suffix on `path`).
   * Used for finance operation transaction grids and similar endpoints.
   */
  postSpecialFilter<T = object>(
    path: string,
    body: object,
    service: ApiServiceKey = 'api'
  ): Observable<BaseResponse<T>> {
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return this.http
      .post<BaseResponse<T>>(this.baseUrlFor(service) + normalized, body)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * POST endpoint with body; returns full response (e.g. for filter that returns { data, query }).
   */
  postWithoutData<T = object>(
    endpoint: string,
    payload: object,
    service: ApiServiceKey = 'api'
  ): Observable<BaseResponse<T>> {
    return this.http
      .post<BaseResponse<T>>(this.baseUrlFor(service) + endpoint, payload)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * PUT endpoint with body; returns response.data.
   */
  updateData<T = ApiMutationData>(endpoint: string, payload: object, service: ApiServiceKey = 'api'): Observable<T> {
    return this.http.put<BaseResponse<T>>(this.baseUrlFor(service) + endpoint, payload).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * PUT `endpoint`/`id` with body; returns response.data.
   * Some gateways allow only POST on `…/{id}` (405 on PUT); use {@link postDataById} for those.
   */
  updateDataById<T = ApiMutationData>(
    endpoint: string,
    id: string | number,
    payload: object,
    service: ApiServiceKey = 'api'
  ): Observable<T> {
    const path = `${endpoint}/${id}`;
    return this.http.put<BaseResponse<T>>(this.baseUrlFor(service) + path, payload).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * DELETE endpoint/id; returns full response.
   */
  deleteData<T = object>(
    endpoint: string,
    id: string | number,
    service: ApiServiceKey = 'api'
  ): Observable<BaseResponse<T>> {
    const path = service === 'fuel' ? `${endpoint}/delete/${id}` : `${endpoint}/${id}`;
    return this.http
      .delete<BaseResponse<T>>(this.baseUrlFor(service) + path)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * POST endpoint/id with body (e.g. container payload); returns response.data.
   */
  postDataByContainer<T = object>(
    endpoint: string,
    id: string | number,
    payload: object,
    service: ApiServiceKey = 'api'
  ): Observable<T> {
    const path = `${endpoint}/${id}`;
    return this.http.post<BaseResponse<T>>(this.baseUrlFor(service) + path, payload).pipe(
      map((response) => (response?.data ?? response) as T),
      catchError((error) => throwError(() => error))
    );
  }

  /** Convenience: base URL for the default API (for components that need to build URLs). */
  get apiUrl(): string {
    return this.baseUrlFor('api');
  }
}
